import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import whobanner from "../img/products.jpg";

const OurProducts = () => {
  const SCROLL_SHOW_THRESHOLD = 300;
  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const RADIUS = 45;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  const featuredProducts = [
    { title: "Shrava360", description: "Streamline HR operations", image: "/img/hrms200 new.png", link: "/Shrava360" },
    { title: "Lauratek", description: "Learning Management System", image: "/img/111.png", link: "/Lauratek" },
    { title: "Logsphere", description: "Workplace Device Insights", image: "/img/85.png", link: "/Logsphere" },
    { title: "Rob n Grab", description: "Smart Recruitment & Onboarding", image: "/img/93.png", link: "/RobnGrab" },
    { title: "OneStep Medi", description: "All-in-One Healthcare Platform", image: "/img/103.png", link: "/one_step_medi" },
    { title: "Lauratek v2.0", description: "Advanced Digital Learning", image: "/img/48.png", link: "/Lauratek2.0" },
    { title: "DevTalent", description: "Skill Evaluation Platform", image: "/img/37.png", link: "/devtalent" },
    { title: "HiSphere", description: "Intelligent On-boarding", image: "/img/213.png", link: "/hisphere" },
    { title: "Vishan ", description: "Smart School Administration", image: "/img/63.png", link: "/Vishan" },
    { title: "Arogya Narayan", description: "Smarter Hospitals for Better Care", image: "/img/53.png", link: "/ArogyaNarayan" },
  ];

  const location = useLocation();

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: false,
      align: 'start',
      dragFree: false,
      containScroll: 'trimSnaps',
    },
    [
      Autoplay({
        delay: 3500,
        stopOnMouseEnter: true,
        stopOnInteraction: false,
      }),
    ]
  );

  // Restore position when returning to this page
  useEffect(() => {
    if (!emblaApi) return;

    // Priority: from navigation state (back/forward button)
    const navIndex = location.state?.fromCarouselIndex;
    if (typeof navIndex === 'number' && navIndex >= 0 && navIndex < featuredProducts.length) {
      emblaApi.scrollTo(navIndex, true); // instant jump on return
      return;
    }

    // Fallback: from sessionStorage (refresh or other cases)
    const saved = sessionStorage.getItem('productsCarouselIndex');
    if (saved !== null) {
      const idx = parseInt(saved, 10);
      if (!isNaN(idx)) {
        emblaApi.scrollTo(idx % featuredProducts.length, true);
      }
    }
  }, [emblaApi, location.state, location.key]);

  // Save current position whenever it changes
  useEffect(() => {
    if (!emblaApi) return;

    const saveIndex = () => {
      const current = emblaApi.selectedScrollSnap();
      sessionStorage.setItem('productsCarouselIndex', String(current));
    };

    emblaApi.on('select', saveIndex);
    emblaApi.on('reInit', saveIndex);

    return () => {
      emblaApi.off('select', saveIndex);
      emblaApi.off('reInit', saveIndex);
    };
  }, [emblaApi]);

  const onScroll = useCallback(() => {
    const sc = window.scrollY || window.pageYOffset;
    setShowScroll(sc > SCROLL_SHOW_THRESHOLD);
    const doc = document.documentElement;
    const totalScroll = Math.max(0, doc.scrollHeight - doc.clientHeight);
    const progress = totalScroll > 0 ? (sc / totalScroll) * 100 : 0;
    setScrollProgress(Math.min(100, Math.max(0, progress)));
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  useEffect(() => {
    window.scrollTo(0, 0);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  const dashOffset = CIRCUMFERENCE - (scrollProgress / 100) * CIRCUMFERENCE;

  const scrollPrev = useCallback(() => {
    if (!emblaApi) return;
    const currentIndex = emblaApi.selectedScrollSnap();
    if (currentIndex === 0) {
      emblaApi.scrollTo(featuredProducts.length - 1);
    } else {
      emblaApi.scrollPrev();
    }
  }, [emblaApi, featuredProducts.length]);

  const scrollNext = useCallback(() => {
    if (!emblaApi) return;
    const currentIndex = emblaApi.selectedScrollSnap();
    if (currentIndex === featuredProducts.length - 1) {
      emblaApi.scrollTo(0);
    } else {
      emblaApi.scrollNext();
    }
  }, [emblaApi, featuredProducts.length]);

  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Helmet>
        <title>Our Products | SecurXpert Technologies</title>
      </Helmet>

      <Navbar />

      <header
        className="relative w-full h-[250px] bg-center bg-cover"
        style={{ backgroundImage: `url(${whobanner})` }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white z-10">
          <h1 className="text-4xl font-bold mb-2">Our Products</h1>
          <div className="flex items-center gap-2 text-sm opacity-80">
            <Link to="/" className="hover:text-blue-400">Home</Link>
            <svg width="7" height="10" viewBox="0 0 7 10" fill="none" className="mt-[2px]">
              <path d="M0.297 0.262C0.105 0.426 -0.002 0.648 0 0.878C0.002 1.109 0.113 1.329 0.308 1.492L4.02 4.604L0.366 7.711C0.173 7.875 0.067 8.097 0.069 8.328C0.071 8.558 0.182 8.778 0.377 8.941C0.474 9.022 0.59 9.086 0.717 9.13C0.844 9.174 0.981 9.196 1.118 9.195C1.255 9.194 1.391 9.171 1.518 9.126C1.644 9.081 1.758 9.016 1.854 8.933L6.232 5.212C6.329 5.131 6.405 5.034 6.457 4.927C6.509 4.821 6.535 4.706 6.534 4.591C6.533 4.476 6.505 4.362 6.451 4.256C6.397 4.15 6.319 4.054 6.221 3.974L1.774 0.254C1.677 0.173 1.561 0.109 1.434 0.065C1.307 0.021 1.171 -0.001 1.033 0C0.896 0.001 0.76 0.024 0.634 0.069C0.507 0.114 0.393 0.18 0.297 0.262Z" fill="white" />
            </svg>
            <span>Our Products</span>
          </div>
        </div>
      </header>

      <section className="py-16 px-6 max-w-[1440px] mx-auto">
        <div className="relative flex flex-col items-center">
          <div
            className="overflow-hidden w-full relative"
            ref={emblaRef}
          >
            <div className="flex gap-5">
              {featuredProducts.map((product, index) => (
                <div
                  key={index}
                  className="min-w-[280px] max-w-[280px] flex-shrink-0"
                >
                  <Link
                    to={product.link}
                    state={{ fromCarouselIndex: index }}
                    className="group block rounded-xl p-4 bg-white shadow-md border border-gray-100
                    hover:shadow-2xl transition-all duration-300 h-full flex flex-col items-center"
                  >
                    <div className="w-full h-36 overflow-hidden rounded-lg mb-3 flex items-center justify-center bg-gray-50">
                      <img
                        src={product.image}
                        alt={product.title}
                        className="h-20 w-20 sm:h-24 sm:w-24 object-contain"
                      />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 text-center leading-tight">
                      {product.title}
                    </h3>
                    <p className="text-gray-500 text-xs text-center mt-1 mb-3 flex-grow">
                      {product.description}
                    </p>
                    <div className="px-4 py-1.5 rounded-full text-white text-sm font-medium bg-gradient-to-r
                      from-[#210A4A] to-[#3E66F3] transform group-hover:scale-105 transition-all">
                      Explore Now
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="products-controls mt-4 flex items-center justify-center gap-3">
            <button
              onClick={scrollPrev}
              className="prev-btn flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 text-white shadow-md shadow-blue-400/30 hover:bg-blue-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-300"
              aria-label="Previous"
            >
              <svg className="w-4.5 h-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>

            <div className="flex items-center justify-center gap-2">
              {featuredProducts.slice(0, -3).map((_, index) => (
                <button
                  key={index}
                  onClick={() => emblaApi?.scrollTo(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                    index === selectedIndex 
                      ? 'w-6 bg-blue-600' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to product ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={scrollNext}
              className="next-btn flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 text-white shadow-md shadow-blue-400/30 hover:bg-blue-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-300"
              aria-label="Next"
            >
              <svg className="w-4.5 h-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      <Footer />

      {showScroll && (
        <div
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 cursor-pointer w-14 h-14 flex items-center justify-center
          rounded-full bg-blue-600 shadow-lg hover:bg-blue-700 transition-all"
        >
          <svg className="-rotate-90 w-12 h-12 absolute" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="6" />
            <circle
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              stroke="white"
              strokeWidth="6"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
            />
          </svg>
          <svg className="w-6 h-6 text-white relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default OurProducts;